import React, { useCallback, useEffect, useRef, useState } from 'react';
import OttoAlive from './OttoAlive';
import OttoDust from './OttoDust';
import { applyTransientClass, beginRoam, HANDOFF_MS, isEventShotActive, onEventShot, sidebarEl } from './ottoStage';
import { isNegativeMood } from './ottoMood';
import { ottoMotionSuppressed } from '../../lib/motion';
import './otto.css';

// The roaming overlay — the surface the Otto idle set travels on when a
// shot needs him outside a component's bounds. Built to the Otto
// architecture notes (2026-08-04); read those before extending this.
//
// PHASE 1 — overlay-only shots. Shots that transform live UI (03 sidebar
// lean, 06 feed bump, 07/08 calendar, 10 nav rows) are deliberately NOT
// here: they need a transient-transform API with a watchdog first, and a
// stuck transform on the primary navigation is the worst realistic
// failure mode in the whole set. Add them behind a flag, 03 last.
//
// Admin-only — App.js mounts this for userType === 'internal' only. Otto
// never appears on a supplier surface.

const SIZE = 88;                              // matches the prototype sprite
const HEIGHT = Math.round(SIZE * 132 / 120);  // Otto's viewBox is 120×132

// Scheduler, per the prototype's own note: "wait a randomised 20-45s of
// real idle before drawing. Gaps stretch as the session goes on."
const IDLE_MIN = 20000;
const IDLE_MAX = 45000;
const DECAY_STEP = 0.18;
const DECAY_CAP = 1.6;
// How long since the last real interaction before the page counts as idle.
const QUIET_MS = 1500;

// ---------------------------------------------------------------------------
// The count has to survive navigation
// ---------------------------------------------------------------------------
// Rob, 2026-08-14: "I don't think he needs to fly in on every page load — it's
// too distracting."
//
// He was right, and the cause was that `played` lived in a ref. The ref dies
// with the component, so every navigation reset the count to zero, the decay
// that is meant to thin him out never accumulated, and each new page started
// the cadence fresh. Someone moving through six pages got the FIRST, most
// frequent gap six times over — the opposite of the intended "he thins out as
// the session goes on".
//
// sessionStorage rather than a module variable: it survives a full reload too,
// so refreshing a page isn't a way to reset him either. Per tab, cleared when
// the tab closes, which is the right lifetime for "this session".
const ROAM_STATE_KEY = 'sp.otto.roam';
const SETTLE_MS = 45000;   // grace after arriving on a page, before he may roam
const CROSS_PAGE_GAP_MS = 120000; // never twice inside two minutes, wherever you are

const readRoamState = () => {
  try {
    const raw = sessionStorage.getItem(ROAM_STATE_KEY);
    const v = raw ? JSON.parse(raw) : null;
    return { played: Number(v?.played) || 0, lastAt: Number(v?.lastAt) || 0 };
  } catch (e) {
    return { played: 0, lastAt: 0 };
  }
};
const writeRoamState = (state) => {
  try { sessionStorage.setItem(ROAM_STATE_KEY, JSON.stringify(state)); } catch (e) { /* private mode */ }
};

// Peek geometry. OVERLAP is how far below the card's top edge his box
// sits at rest — it must exceed HEIGHT so he's FULLY hidden before the
// rise, and stay small enough that the settled pose clears the edge by
// the ≥15px the brief demands (at -79px he clears by ~22px here).
const PEEK_OVERLAP = 100;
const PEEK_RISE = 88;                                  // overshoot peak
const PEEK_CLEARANCE = PEEK_OVERLAP + PEEK_RISE - HEIGHT + 15; // needed above the edge

// Was a local copy of the reduced-motion predicate. Now the shared one, which
// also answers for the per-user Otto toggle -- a roamer that kept scheduling
// would still reposition him for motion the user asked not to see.
const prefersReducedMotion = ottoMotionSuppressed;
const isTouch = () =>
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);

// A shot must not start while anything is trapping focus. Modal,
// ConfirmDialog and SideDrawer all carry role="dialog"/aria-modal.
const modalOpen = () =>
  Boolean(document.querySelector('[role="dialog"], [aria-modal="true"]'));

const fieldFocused = () => {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

// Card targets are resolved from LIVE rects at shot start, never baked.
// The prototype's coordinates are correct for its 1180×580 field and
// wrong for production — they break at other viewport widths, with
// permissioned navigation, and on any page whose layout isn't the
// dashboard. `.shadow-card` is the app's card treatment; opt a card out
// with data-otto-skip.
const resolvePeekTarget = () => {
  const cards = Array.from(document.querySelectorAll('.shadow-card:not([data-otto-skip])'));
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const usable = cards.filter((el) => {
    const r = el.getBoundingClientRect();
    if (r.width < SIZE * 1.6 || r.height < 60) return false;      // too small to hide behind
    if (r.top < PEEK_CLEARANCE || r.top > vh - 80) return false;  // no room above / off-screen
    if (r.left < 0 || r.right > vw) return false;
    // The air he rises into must be clear. If the midpoint above this card
    // lands inside another card, he'd surface on top of it.
    const probe = document.elementFromPoint(
      Math.round(r.left + r.width / 2),
      Math.round(r.top - PEEK_CLEARANCE / 2),
    );
    if (probe && probe.closest('.shadow-card')) return false;
    return true;
  });
  if (!usable.length) return null;                 // no target → suppress the shot
  const card = usable[Math.floor(Math.random() * usable.length)];
  const r = card.getBoundingClientRect();
  const left = Math.round(r.left + r.width * 0.62 - SIZE / 2); // right-of-centre, like the prototype
  const top = Math.round(r.top + PEEK_OVERLAP - (PEEK_RISE + HEIGHT));
  return {
    style: {
      left: `${left}px`,
      top: `${top}px`,
      width: `${SIZE}px`,
      height: `${PEEK_RISE + HEIGHT}px`,
      // Hide everything at or below the card's top edge. The wrapper is
      // stationary, so this clip does not travel with the rise.
      clipPath: `inset(0 0 ${PEEK_OVERLAP}px 0)`,
    },
  };
};

// Home-anchored body shots. Otto drops into his corner (bottom-left, against
// the sidebar, like the prototype) and plays ONE of his existing, prop-free
// idle motions — reusing OttoAlive's proven repertoire rather than authoring
// new travelling keyframes. Durations mirror OttoAlive's own DUR table (+ a
// short tail so the rest pose reads before he clears).
const HOME_ANIMS = ['stretch', 'headtilt', 'shiver', 'perk', 'surprise', 'preen'];
const HOME_DUR = { stretch: 1400, headtilt: 1600, shiver: 600, perk: 520, surprise: 520, preen: 1560 };

// Otto's resting spot: bottom-left of the content, just clear of the sidebar's
// live right edge (never a baked coordinate — the sidebar width and presence
// vary by viewport and permissions).
const homeLeft = (side, vw, gap = 20) => {
  const sideRight = side ? Math.max(0, side.getBoundingClientRect().right) : 0;
  const left = Math.round(Math.max(16, sideRight + gap));
  return left > vw - SIZE - 16 ? null : left;
};

const resolveHomeTarget = () => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = homeLeft(sidebarEl(), vw);
  if (left == null || vh < HEIGHT + 60) return null; // no room
  const top = Math.round(vh - HEIGHT - 22);
  return { style: { left: `${left}px`, top: `${top}px`, width: `${SIZE}px`, height: `${HEIGHT}px` } };
};

// 03 lean/wobble: he must START far enough right that the -48px travel lands
// him ON the rail's edge rather than deep inside it. The prototype's 38px gap
// against its 158px sidebar is the geometry that makes contact read, so this
// shot uses that gap instead of home's 20px.
const LEAN_GAP = 38;
const LEAN_TRAVEL = 48;

const resolveLeanTarget = () => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const side = sidebarEl();
  if (!side) return null;
  const sr = side.getBoundingClientRect();
  // A collapsed or off-canvas rail is not something to lean on.
  if (sr.width < 120 || sr.right <= 0) return null;
  // Never wobble a rail the user is actually working in — an open user menu
  // or a focused nav search would be yanked sideways underneath them.
  if (side.contains(document.activeElement)) return null;
  const left = homeLeft(side, vw, LEAN_GAP);
  if (left == null) return null;
  // He needs the rail plus the floor he hops across to reach it.
  if (left - LEAN_TRAVEL < sr.left) return null;
  if (vh < HEIGHT + 90) return null;
  const top = Math.round(vh - HEIGHT - 22);
  return {
    style: { left: `${left}px`, top: `${top}px`, width: `${SIZE}px`, height: `${HEIGHT}px` },
    sidebar: side,
  };
};

// 09 preen on the ledge: he perches in the empty stretch between the last nav
// item and the user card. That gap is the ONLY clear ledge in the chrome, and
// with a longer nav — more items, or different permissions — it closes and he
// has nowhere to stand, so it is measured live and the shot suppresses itself
// rather than perching on a nav row or over the user's own card (brief §8.1).
//
// The nav is flex-1, so its box always runs down to the user card: the free
// ledge is exactly its unused inner height, and an overflowing nav yields a
// negative number and suppresses on the same test.
const LEDGE_MIN_GAP = 95;

const resolveLedgeTarget = () => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const side = sidebarEl();
  if (!side) return null;
  const nav = side.querySelector('nav');
  if (!nav) return null;
  const gap = nav.clientHeight - nav.scrollHeight;
  if (gap < LEDGE_MIN_GAP) return null;             // nowhere to stand
  const sr = side.getBoundingClientRect();
  const nr = nav.getBoundingClientRect();
  if (sr.width < 120 || sr.right <= 0) return null;
  const home = homeLeft(side, vw);
  if (home == null || vh < HEIGHT + 60) return null;
  const homeTop = Math.round(vh - HEIGHT - 22);
  // Centred on the rail, feet on the user card's top edge (= the nav's bottom,
  // they are adjacent flex siblings).
  const ledgeLeft = Math.round(sr.left + (sr.width - SIZE) / 2);
  const ledgeTop = Math.round(nr.bottom - HEIGHT);
  if (ledgeTop < 0) return null;
  return {
    style: {
      left: `${home}px`, top: `${homeTop}px`, width: `${SIZE}px`, height: `${HEIGHT}px`,
      '--ledge-x': `${ledgeLeft - home}px`,
      '--ledge-y': `${ledgeTop - homeTop}px`,
    },
  };
};

// 01 fly-up: takes off from home and climbs the sidebar. The climb height is
// the live gap from his resting top up to ~40px below the viewport top, handed
// to the CSS as --fly-rise so the keyframes scale to the real sidebar rather
// than the prototype's baked field. Suppressed on short viewports.
const resolveFlyTarget = () => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = homeLeft(sidebarEl(), vw);
  if (left == null) return null;
  const top = Math.round(vh - HEIGHT - 22);
  const rise = top - 40;
  if (rise < 220) return null;                       // too short to be worth it
  return {
    style: {
      left: `${left}px`, top: `${top}px`, width: `${SIZE}px`, height: `${HEIGHT}px`,
      '--fly-rise': `${rise}px`,
    },
  };
};

// 07 perch-and-slip: Otto stands ON a card's top edge (fully visible, no clip),
// so we need a card with a full body-height of clear air above it. Feet land at
// the card's top edge; the slip pivots on those feet.
const resolvePerchTarget = () => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cards = Array.from(document.querySelectorAll('.shadow-card:not([data-otto-skip])'));
  const usable = cards.filter((el) => {
    const r = el.getBoundingClientRect();
    if (r.width < SIZE * 1.6 || r.height < 60) return false;
    if (r.top < HEIGHT + 20 || r.top > vh - 80) return false;   // need a full body above
    if (r.left < 0 || r.right > vw) return false;
    const probe = document.elementFromPoint(
      Math.round(r.left + r.width / 2),
      Math.round(r.top - HEIGHT / 2),
    );
    if (probe && probe.closest('.shadow-card')) return false;
    return true;
  });
  if (!usable.length) return null;
  const card = usable[Math.floor(Math.random() * usable.length)];
  const r = card.getBoundingClientRect();
  const left = Math.round(r.left + r.width * 0.5 - SIZE / 2);
  const top = Math.round(r.top - HEIGHT);                       // feet on the top edge
  return { style: { left: `${left}px`, top: `${top}px`, width: `${SIZE}px`, height: `${HEIGHT}px` } };
};

// 10 perch-down-nav: Otto flies to the top main-menu row, hops down three
// rows perching on each row's TOP EDGE, dipping ONLY that row as he lands, then
// drops off and hops home. Two traps carried from the prototype build notes:
//   • Scope to the MAIN menu only. An unscoped nav-row selector also dips the
//     System rows (Reporting/Documents/Admin) — Sidebar marks the main section
//     with data-otto-navsection="main" precisely so this can't over-reach.
//   • Perch heights are COMPUTED from live rows (spacing/contents vary by role
//     and viewport), never baked; the shot suppresses under three rows.
const NAV_ROWS = 3;
// Landing fractions of the shot's duration — when Otto's feet meet each row, so
// the row-dip fires in sync with the actor keyframe's landings.
const NAV_LAND = [0.14, 0.36, 0.58];

const resolveNavTarget = () => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const side = sidebarEl();
  if (!side) return null;
  const sr = side.getBoundingClientRect();
  if (sr.width < 120 || sr.right <= 0) return null;
  // Top-level rows of the MAIN section only ([section] > [row div] > button).
  const rows = Array.from(side.querySelectorAll('[data-otto-navsection="main"] > div > button'));
  if (rows.length < NAV_ROWS) return null;                 // nothing to hop down
  const use = rows.slice(0, NAV_ROWS);
  const rects = use.map((el) => el.getBoundingClientRect());
  // Rows must be on-screen, sensibly sized and in descending order — a
  // collapsed or scrolled nav isn't a staircase.
  for (let i = 0; i < rects.length; i += 1) {
    const r = rects[i];
    if (r.height < 20 || r.top < 0 || r.bottom > vh) return null;
    if (i > 0 && r.top <= rects[i - 1].top) return null;
  }
  const home = homeLeft(side, vw);
  if (home == null || vh < HEIGHT + 60) return null;
  const homeTop = Math.round(vh - HEIGHT - 22);
  const navX = Math.round(sr.left + (sr.width - SIZE) / 2);
  // Feet land on each row's top edge: box bottom (top + HEIGHT) meets row.top.
  const yFor = (r) => Math.round((r.top - HEIGHT) - homeTop);
  return {
    style: {
      left: `${home}px`, top: `${homeTop}px`, width: `${SIZE}px`, height: `${HEIGHT}px`,
      '--nav-x': `${navX - home}px`,
      '--nav-y0': `${yFor(rects[0])}px`,
      '--nav-y1': `${yFor(rects[1])}px`,
      '--nav-y2': `${yFor(rects[2])}px`,
    },
    navRows: use,
  };
};

// Dip each perched row 2.5px as Otto lands on it. Every dip goes through
// applyTransientClass, so each is individually watchdogged and force-cleared —
// no path can leave a nav row transformed. Returns a composite release that
// cancels the pending dips and clears any already applied.
const applyNavDips = (rows, dur) => {
  const timers = [];
  const releases = [];
  rows.forEach((el, i) => {
    timers.push(setTimeout(() => {
      releases.push(applyTransientClass(el, 'otto-nav-dip', 500));
    }, Math.round((NAV_LAND[i] || 0) * dur)));
  });
  return () => {
    timers.forEach(clearTimeout);
    releases.forEach((r) => r());
  };
};

// 08 · Wing caught between cards. Needs a REAL gap between two cards sitting
// side by side on the same row — the gag is that his wing is trapped in it, so
// an invented gap would be a lie about the layout. The gap has to be wide
// enough to read as a gap and narrow enough that a wing plausibly jams in it,
// and there has to be floor under it for him to stand on.
const SNAG_GAP_MIN = 8;
const SNAG_GAP_MAX = 44;

const resolveSnagTarget = () => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cards = Array.from(document.querySelectorAll('.shadow-card:not([data-otto-skip])'))
    .map((el) => ({ el, r: el.getBoundingClientRect() }))
    .filter(({ r }) => r.width > 140 && r.height > 60 && r.top > 0 && r.bottom < vh);
  if (cards.length < 2) return null;

  const pairs = [];
  for (let i = 0; i < cards.length; i += 1) {
    for (let j = 0; j < cards.length; j += 1) {
      if (i === j) continue;
      const a = cards[i].r;
      const b = cards[j].r;
      const gap = b.left - a.right;
      if (gap < SNAG_GAP_MIN || gap > SNAG_GAP_MAX) continue;
      // Same row: their vertical spans have to overlap substantially, or
      // "between two cards" is really "past the corner of one".
      const overlap = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
      if (overlap < Math.min(a.height, b.height) * 0.6) continue;
      pairs.push({ left: cards[i], right: cards[j], gap, mid: a.right + gap / 2 });
    }
  }
  if (!pairs.length) return null;

  // Prefer a pair near the middle of the viewport — a gap at the very edge
  // puts half of him off-screen when he staggers back.
  pairs.sort((p, q) => Math.abs(p.mid - vw / 2) - Math.abs(q.mid - vw / 2));
  const pick = pairs[0];
  const rowTop = Math.max(pick.left.r.top, pick.right.r.top);

  // He stands with his RIGHT wing at the gap, so his box sits to the left of
  // it. Feet on the cards' top edge.
  const left = Math.round(pick.mid - SIZE * 0.62);
  const top = Math.round(rowTop - HEIGHT);
  // The stagger travels 52px left; if that would push him off-screen or he'd
  // sit above the fold, there's no room to play it.
  if (left - 52 < 4 || top < 4) return null;

  return {
    style: { left: `${left}px`, top: `${top}px`, width: `${SIZE}px`, height: `${HEIGHT}px` },
    card: pick.right.el,
  };
};

// 15 · Three Ottos (rare). He glitches, splits into three, they notice each
// other one at a time, all squawk together, and two fly off leaving the
// original to shrug.
//
// SPLITS RIGHTWARDS ONLY, deliberately (brief §6). A symmetric split puts the
// leftmost copy over the dark sidebar — a brown owl on near-black, unreadable
// — and this way the lead is the leftmost, which gives the overhead crossing
// somewhere to happen.
//
// Needs ~450px of clear width to the right of home for the two copies at
// +112 and +224 plus his own body, and headroom for the departure.
const TRIO_SPAN = 450;
const TRIO_HEADROOM = 150;

const resolveTrioTarget = () => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = homeLeft(sidebarEl(), vw);
  if (left == null) return null;
  if (left + TRIO_SPAN > vw - 16) return null;          // no room to split into
  // They exit up and off-screen (brief §9.3): on a short viewport they would
  // clip the top before they had faded, so the shot suppresses rather than
  // playing badly.
  if (vh < HEIGHT + TRIO_HEADROOM + 60) return null;
  const top = Math.round(vh - HEIGHT - 22);
  // The clip has to be wide enough to hold all three, or the copies are cut
  // off at its edge. Height is generous for the same reason on the departure.
  return {
    style: {
      left: `${left}px`,
      top: `${top - TRIO_HEADROOM}px`,
      width: `${TRIO_SPAN}px`,
      height: `${HEIGHT + TRIO_HEADROOM}px`,
    },
  };
};

// Shot registry. `weight` mirrors the prototype's draw odds; note those
// are tuned for the full 17-shot set and want retuning while only a
// subset ships.
const SHOTS = [
  { id: 'peek', cls: 'otto-roam-peek', dur: 2400, weight: 16, resolve: resolvePeekTarget },
  { id: 'home', home: true, dur: 2000, weight: 20, resolve: resolveHomeTarget },
  { id: 'fly', cls: 'otto-roam-fly', dur: 5800, weight: 8, resolve: resolveFlyTarget },
  { id: 'perch', cls: 'otto-roam-perch', dur: 2800, weight: 9, resolve: resolvePerchTarget },
  { id: 'glass', cls: 'otto-roam-glass', dur: 5400, fog: true, weight: 7, resolve: resolveHomeTarget },
  // The only shot that transforms live UI. `sidebar: true` routes it through
  // applyTransientClass so the rail can never keep the transform.
  { id: 'lean', cls: 'otto-roam-lean', dur: 5600, weight: 12, sidebar: true, dust: '2.66s', resolve: resolveLeanTarget },
  { id: 'ledge', cls: 'otto-roam-ledge', dur: 4200, weight: 8, resolve: resolveLedgeTarget },
  // Transforms live UI (the nav rows), so it routes through applyNavDips —
  // each dip watchdogged like the sidebar give. Modest weight: it's the
  // longest, loudest and riskiest shot, so it stays a rare treat.
  { id: 'nav', cls: 'otto-roam-nav', dur: 5400, weight: 6, navRows: true, resolve: resolveNavTarget },
  // Also transforms live UI — the card he tugs against springs with him — so
  // it routes through the same watchdogged transient class as the rail.
  { id: 'snag', cls: 'otto-roam-snag', dur: 3600, weight: 9, card: true, resolve: resolveSnagTarget },
  // RARE. The brief asks for ~0.2% of draws ("once per several hours of active
  // use") and quotes weight 0.3 — but that number was computed for the full
  // 17-shot set. The sneeze brief is explicit that a subset must RECOMPUTE
  // rather than inherit, and at nine shots the denominator is far smaller:
  // 0.3 would land near 0.3%. 0.2 gives 2 entries in ~952, or 0.21% — the
  // frequency the brief actually asks for. Revisit when the rest of the set
  // ships.
  { id: 'trio', cls: 'otto-roam-trio', dur: 7400, weight: 0.2, trio: true, resolve: resolveTrioTarget },
];

export default function OttoRoamer() {
  const [shot, setShot] = useState(null); // { cls, style }
  const bagRef = useRef([]);
  const recentRef = useRef([]);
  const playedRef = useRef(0);
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef(null);
  const runningRef = useRef(false);
  const shotTimerRef = useRef(null);
  // Release for any live-UI transform the running shot applied.
  const releaseRef = useRef(null);
  // Hands the stage back so the perched Otto flies home. Must run on EVERY
  // exit path — an empty briefing bar is a worse bug than a missed animation.
  const endRoamRef = useRef(null);
  const departTimerRef = useRef(null);

  // Everything that ends a shot early goes through here, so the rail can never
  // be left mid-give by a path that forgot to clean up.
  const clearShot = useCallback(() => {
    clearTimeout(shotTimerRef.current);
    clearTimeout(departTimerRef.current);
    if (releaseRef.current) { releaseRef.current(); releaseRef.current = null; }
    if (endRoamRef.current) { endRoamRef.current(); endRoamRef.current = null; }
    setShot(null);
    runningRef.current = false;
  }, []);

  // Shuffle bag rather than plain weighted random — plain random repeats
  // far more than people expect. The recent-history guard stops the same
  // shot landing twice running.
  const draw = useCallback(() => {
    if (!bagRef.current.length) {
      const bag = [];
      SHOTS.forEach((s) => {
        // Scaled by 10 so sub-integer weights survive. Rounding first made
        // every rare shot (0.2, 0.3) collapse onto the 1-entry floor, which
        // is the same share as a weight-1 shot — five times too often.
        for (let i = 0; i < Math.max(1, Math.round(s.weight * 10)); i += 1) bag.push(s.id);
      });
      for (let i = bag.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [bag[i], bag[j]] = [bag[j], bag[i]];
      }
      bagRef.current = bag;
    }
    for (let k = 0; k < 12; k += 1) {
      const id = bagRef.current.pop();
      if (!id) break;
      if (SHOTS.length === 1 || recentRef.current.indexOf(id) === -1) {
        recentRef.current.push(id);
        if (recentRef.current.length > 3) recentRef.current.shift();
        return id;
      }
    }
    return SHOTS[Math.floor(Math.random() * SHOTS.length)].id;
  }, []);

  useEffect(() => {
    if (prefersReducedMotion() || isTouch()) return undefined;

    const bump = () => { lastActivityRef.current = Date.now(); };
    const events = ['keydown', 'wheel', 'scroll', 'pointermove', 'pointerdown', 'focusin'];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    let cancelled = false;

    // Arriving on a page is not an invitation to perform. He settles first.
    const mountedAt = Date.now();

    const schedule = () => {
      clearTimeout(timerRef.current);
      // Gaps stretch as the SESSION goes on — read from storage, so navigating
      // doesn't hand him a fresh, fast cadence on every page.
      const { played } = readRoamState();
      const decay = 1 + Math.min(played * DECAY_STEP, DECAY_CAP);
      const gap = (IDLE_MIN + Math.random() * (IDLE_MAX - IDLE_MIN)) * decay;
      timerRef.current = setTimeout(() => {
        if (cancelled) return;
        // Every gate is re-checked at fire time, not at schedule time.
        // isEventShotActive: a request in flight owns Otto — the head-cock is
        // tied to something the user did, a roam is decoration, so decoration
        // yields (head-cock brief §8.1).
        // isNegativeMood is the only TONAL gate. Every other check here is
        // technical, and a screen full of overdue work passes all of them while
        // still being the wrong place for him to hop across. Surfaces declare it
        // themselves (ottoMood.js); nothing is inferred from the DOM.
        if (document.hidden || modalOpen() || fieldFocused() || runningRef.current
            || isEventShotActive() || isNegativeMood()
            || Date.now() - lastActivityRef.current < QUIET_MS) {
          schedule();
          return;
        }
        // Two gates that only make sense across navigations, and are the
        // actual fix for "he flies in on every page load":
        //   · a settling period after arriving, so landing on a page and
        //     reading it isn't immediately interrupted;
        //   · a floor on the gap since his last roam ANYWHERE, so six quick
        //     page changes can't become six performances.
        const { lastAt } = readRoamState();
        if (Date.now() - mountedAt < SETTLE_MS
            || (lastAt && Date.now() - lastAt < CROSS_PAGE_GAP_MS)) {
          schedule();
          return;
        }
        const spec = SHOTS.find((s) => s.id === draw());
        const target = spec && spec.resolve();
        if (!target) { schedule(); return; }   // no usable target → suppress, try later
        runningRef.current = true;
        playedRef.current += 1;
        // Recorded in session storage, not just the ref, so the decay and the
        // cross-page gap both survive navigating away from this page.
        writeRoamState({ played: readRoamState().played + 1, lastAt: Date.now() });
        // Home shots pick one of Otto's body motions at fire time; the shot's
        // length follows that motion. Overlay shots (peek) carry a fixed CSS
        // duration.
        let dur = spec.dur;
        let play = null;
        if (spec.home) {
          play = HOME_ANIMS[Math.floor(Math.random() * HOME_ANIMS.length)];
          dur = (HOME_DUR[play] || 1600) + 500;
        }
        // ONE OTTO. Hand the stage over first: the perched Otto flies off, and
        // only then does the roaming one appear. Without the delay both are on
        // screen together for the length of the departure, which is the exact
        // thing this fixes.
        endRoamRef.current = beginRoam(dur);
        // Everything the shot does — the sprite AND any live-UI transform —
        // waits for the perched Otto to leave. Moving a card before he's there
        // to move it is the tell that they're two different owls.
        departTimerRef.current = setTimeout(() => {
          if (cancelled) return;
          setShot({ cls: spec.cls || '', style: target.style, play, fog: !!spec.fog, dust: spec.dust || null, trio: !!spec.trio });
          // Live-UI transforms, watchdogged, released by clearShot on every
          // exit path.
          if (spec.sidebar && target.sidebar) {
            releaseRef.current = applyTransientClass(target.sidebar, 'otto-sidebar-give', dur);
          }
          if (spec.navRows && target.navRows) {
            releaseRef.current = applyNavDips(target.navRows, dur);
          }
          if (spec.card && target.card) {
            releaseRef.current = applyTransientClass(target.card, 'otto-card-nudge', dur);
          }
        }, HANDOFF_MS);
        shotTimerRef.current = setTimeout(() => {
          if (cancelled) return;
          clearShot();                         // also hands the stage back
          schedule();                          // idle timer resets on completion
        }, HANDOFF_MS + dur + 60);
      }, gap);
    };
    schedule();

    const onVisibility = () => {
      if (document.hidden) { clearTimeout(timerRef.current); clearShot(); }
      else schedule();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // A request starting mid-roam pre-empts it: drop the idle shot at once
    // (releasing the rail with it), and restart the idle clock from zero when
    // the request finishes rather than resuming a gap that elapsed meanwhile.
    const offEventShot = onEventShot((phase) => {
      if (cancelled) return;
      if (phase === 'start') { clearTimeout(timerRef.current); clearShot(); }
      else schedule();
    });

    // The shot ids, published so the QA surfaces don't keep their own copy of
    // the list. OttoDevPanel renders from this, so a shot added to SHOTS shows
    // up there without anyone remembering to add it twice.
    window.__ottoShots = SHOTS.map((s) => s.id);

    // Preview trigger — run `__ottoPlay('fly')` (or 'peek' / 'home') from the
    // console, drive it from the dev Otto gallery, or use the hidden in-app
    // panel (OttoDevPanel), to fire a shot on demand instead of waiting on the
    // idle timer. Bypasses the gates; harmless (the roamer only mounts for
    // internal users) and used by the QA surfaces.
    window.__ottoPlay = (id) => {
      const spec = SHOTS.find((s) => s.id === id);
      if (!spec) { return `unknown shot; try: ${SHOTS.map((s) => s.id).join(', ')}`; }
      const target = spec.resolve();
      if (!target) return 'no usable target on this screen';
      let dur = spec.dur;
      let play = null;
      if (spec.home) {
        play = HOME_ANIMS[Math.floor(Math.random() * HOME_ANIMS.length)];
        dur = (HOME_DUR[play] || 1600) + 500;
      }
      // Same handoff as the scheduled path, so previewing shows what actually
      // ships rather than a version with two Ottos in it.
      endRoamRef.current = beginRoam(dur);
      departTimerRef.current = setTimeout(() => {
        setShot({ cls: spec.cls || '', style: target.style, play, fog: !!spec.fog, dust: spec.dust || null, trio: !!spec.trio });
        if (spec.sidebar && target.sidebar) {
          releaseRef.current = applyTransientClass(target.sidebar, 'otto-sidebar-give', dur);
        }
        if (spec.navRows && target.navRows) {
          releaseRef.current = applyNavDips(target.navRows, dur);
        }
        if (spec.card && target.card) {
          releaseRef.current = applyTransientClass(target.card, 'otto-card-nudge', dur);
        }
      }, HANDOFF_MS);
      shotTimerRef.current = setTimeout(clearShot, HANDOFF_MS + dur + 60);
      return `playing ${id}${play ? ` (${play})` : ''}`;
    };

    return () => {
      cancelled = true;
      clearTimeout(timerRef.current);
      clearShot();                    // never unmount holding a rail transform
      events.forEach((e) => window.removeEventListener(e, bump));
      document.removeEventListener('visibilitychange', onVisibility);
      offEventShot();
      delete window.__ottoPlay;
    };
  }, [draw, clearShot]);

  if (!shot) return null;

  return (
    <div className={`otto-roamer ${shot.cls}`} aria-hidden="true">
      <div className="otto-roam-clip" style={shot.style}>
        {shot.fog && <div className="otto-roam-fog" />}
        {shot.trio ? (
          // 15 · Three Ottos. FOUR transform layers per copy, because the brief
          // drives four independent tracks and stacking two onto one element is
          // how they cancel each other:
          //   .otto-trio-<role>  split translate
          //   .otto-trio-hop     squawk jolt (translateY)
          //   .otto-roam-tilt    the staggered turns (rotate)
          //   .otto-svg          the squawk body scale, inside OttoAlive
          //
          // Roles are lead / mid / far, left to right. The LEAD NEVER FLIES —
          // he is the one left behind, and that is the ending.
          ['lead', 'mid', 'far'].map((role) => (
            <div key={role} className={`otto-roam-actor otto-trio-${role}`}>
              <div className="otto-trio-hop">
                {/* The alarm mark. Three of them, staggered 30ms apart in CSS so
                    it reads as three birds rather than one sound effect. A mark
                    rather than text: it sidesteps the alt-text question that a
                    written "squawk" would raise. */}
                <span className="otto-trio-mark" aria-hidden="true">
                  <svg width="34" height="26" viewBox="0 0 34 26">
                    {/* Strokes RADIATE from a gap and must not converge on a
                        point — an earlier version met at a vertex below and read
                        as three arrows aimed at their heads (brief §5). */}
                    <g stroke="#E89A2E" strokeWidth="2.4" strokeLinecap="round">
                      <line x1="17" y1="8" x2="17" y2="1" />
                      <line x1="10" y1="10" x2="5" y2="4" />
                      <line x1="24" y1="10" x2="29" y2="4" />
                      <line x1="7" y1="16" x2="1" y2="17" />
                      <line x1="27" y1="16" x2="33" y2="17" />
                    </g>
                  </svg>
                </span>
                <div className="otto-roam-tilt">
                  <OttoAlive size={SIZE} interactive={false} ariaLabel="" />
                </div>
              </div>
            </div>
          ))
        ) : (
        <div className="otto-roam-actor">
          {/* Dust rides the ACTOR, not the tilt — it marks where his feet
              scrabble, so it must travel with him but not rotate with him. */}
          {shot.dust && <OttoDust delay={shot.dust} />}
          <div className="otto-roam-tilt">
            {/* interactive={false} — the roaming Otto is decorative and the
                layer is pointer-events:none, so there is nothing to click.
                OttoAlive (not the static Otto) because only it renders the
                .otto-pupils group the eye-scan drives. `play` fires a body
                motion for the home shots; null (peek) is a no-op. */}
            <OttoAlive size={SIZE} interactive={false} play={shot.play || null} ariaLabel="" />
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
